import type { LiquidityScoreResponse } from '@/pages/api/liquidity-score';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { fetchWithJwt, getFullURL, handleResponse } from '../utils/fetcher';
import { normalizeTokenAddress, tokenKey } from '../utils/normalizeAddress';

export const liquidityScoreQueryKey = (tokensParam: string | null) =>
  ['liquidity-score', tokensParam] as const;

// Auto-slippage quote builders hold their fetch on this query settling, so a hung
// request must fail fast (-> resident-signal fallback) instead of stalling quotes.
const LIQUIDITY_SCORE_TIMEOUT_MS = 3_000;

// Scores move on the order of hours; keep repeat mounts (settings modal, checkout)
// from re-firing the same request.
const LIQUIDITY_SCORE_STALE_TIME_MS = 5 * 60 * 1000;

/** Exported for unit testing. Aborts (and rejects) after {@link LIQUIDITY_SCORE_TIMEOUT_MS}. */
export const fetchLiquidityScore = (
  tokensParam: string,
  signal?: AbortSignal,
): Promise<LiquidityScoreResponse> => {
  const controller = new AbortController();
  const onAbort = () => controller.abort();
  signal?.addEventListener('abort', onAbort);
  const timeoutId = setTimeout(() => controller.abort(), LIQUIDITY_SCORE_TIMEOUT_MS);

  return fetchWithJwt(getFullURL(`/api/liquidity-score?tokens=${tokensParam}`), {
    signal: controller.signal,
  })
    .then(handleResponse<LiquidityScoreResponse>)
    .finally(() => {
      clearTimeout(timeoutId);
      signal?.removeEventListener('abort', onAbort);
    });
};

/**
 * Fetches liquidity scores for a batch of tokens using the upstream batch API.
 * @param tokens - Array of { address, chainId } to fetch scores for.
 * @returns Normalized score data. Scores of -1 are mapped to undefined.
 */
export const useLiquidityScore = (
  tokens: Array<{ address: string; chainId: number }> | undefined,
) => {
  // Build a stable query key by sorting tokens deterministically
  const tokensParam = useMemo(() => {
    if (!tokens || tokens.length === 0) return null;

    const sorted = [...tokens].sort((a, b) => {
      const chainDiff = a.chainId - b.chainId;
      if (chainDiff !== 0) return chainDiff;
      return normalizeTokenAddress(a.address, a.chainId).localeCompare(
        normalizeTokenAddress(b.address, b.chainId),
      );
    });

    return sorted.map((t) => tokenKey(t.address, t.chainId)).join(',');
  }, [tokens]);

  const { data, error, isLoading, isFetching, refetch } = useQuery<LiquidityScoreResponse, Error>({
    queryKey: liquidityScoreQueryKey(tokensParam),
    queryFn: ({ signal }) => fetchLiquidityScore(tokensParam as string, signal),
    enabled: tokensParam !== null,
    placeholderData: keepPreviousData,
    staleTime: LIQUIDITY_SCORE_STALE_TIME_MS,
    retry: false,
    refetchOnReconnect: false,
  });

  // Normalize: map -1 scores to undefined
  const normalizedData = useMemo(
    () =>
      data
        ? {
            ...data,
            data: data.data.map((item) => ({
              ...item,
              score: item.score === -1 ? undefined : item.score,
            })),
          }
        : undefined,
    [data],
  );

  return {
    data: normalizedData,
    error,
    isLoading,
    isFetching,
    refetch,
  };
};
